import * as React from 'react';
import classnames from 'classnames';

import ContentGalleryContext from './context/ContentGalleryContext';
import ContentGalleryItem from './components/item/ContentGalleryItem';

import { prepareItemsForGallery } from './utils/content-gallery';

import './content-gallery.scss';

import type {
  ContentGalleryAssetOverlay,
  ContentGalleryContext as ContentGalleryContextAlias,
  ContentGalleryItem as ContentGalleryItemAlias,
  ContentGalleryItemSize,
  ContentGalleryThumbnailOverlay,
  ContentGalleryTriggerFooter,
  ContentGalleryTriggerOverlay
} from './types';

type Props = {
  items: Array<ContentGalleryItemAlias>,
  itemSize?: ContentGalleryItemSize,
  renderGalleryAssetOverlay?: ContentGalleryAssetOverlay,
  renderGalleryThumbnailOverlay?: ContentGalleryThumbnailOverlay,
  renderTriggerOverlay?: ContentGalleryTriggerOverlay,
  renderTriggerPopover?: () => React.ReactNode,
  renderTriggerFooter?: ContentGalleryTriggerFooter
};

const ContentGallery: React.FunctionComponent<Props> = ({
  items,
  itemSize = 'size-150',
  renderGalleryAssetOverlay,
  renderGalleryThumbnailOverlay,
  renderTriggerOverlay,
  renderTriggerPopover,
  renderTriggerFooter
}: Props) => {
  const galleryItems = prepareItemsForGallery(items);

  const containerClasses = classnames('content-gallery', {
    [`content-gallery--${itemSize}`]: Boolean(itemSize)
  });

  const context = React.useMemo<ContentGalleryContextAlias>(() => ({
    items,
    itemSize,
    renderGalleryAssetOverlay,
    renderGalleryThumbnailOverlay,
    renderTriggerOverlay,
    renderTriggerPopover,
    renderTriggerFooter
  }), [
    items,
    itemSize,
    renderGalleryAssetOverlay,
    renderGalleryThumbnailOverlay,
    renderTriggerOverlay,
    renderTriggerPopover,
    renderTriggerFooter
  ]);

  return (
    <ContentGalleryContext.Provider value={context}>
      <div className={containerClasses}>
        {
          items.map((item, index) => (
            <ContentGalleryItem
              key={`content-gallery-item--${item.id}`}
              item={item}
              galleryItems={galleryItems}
              galleryItemIndex={index}
            />
          ))
        }
      </div>
    </ContentGalleryContext.Provider>
  );
};

export default ContentGallery;
